import { createContext, useContext, type ReactNode } from "react";

export interface RuntimeConfig {
  siteUrl: string;
  apiUrl: string;
  landingEmbedKey: string;
  cognito: { authority: string; clientId: string; domain: string };
}

function isFilled(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

const withoutTrailingSlash = (value: string) => value.replace(/\/+$/, "");

/** Runtime settings come from /config.json (written by Terraform) so one build serves every environment. */
export function parseConfig(raw: unknown): RuntimeConfig {
  const value = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const cognito = (typeof value.cognito === "object" && value.cognito !== null ? value.cognito : {}) as Record<
    string,
    unknown
  >;
  if (
    !isFilled(value.siteUrl) ||
    !isFilled(value.apiUrl) ||
    !isFilled(value.landingEmbedKey) ||
    !isFilled(cognito.authority) ||
    !isFilled(cognito.clientId) ||
    !isFilled(cognito.domain)
  ) {
    throw new Error("config.json is missing required settings");
  }
  return {
    siteUrl: withoutTrailingSlash(value.siteUrl),
    apiUrl: withoutTrailingSlash(value.apiUrl),
    landingEmbedKey: value.landingEmbedKey,
    cognito: {
      authority: cognito.authority,
      clientId: cognito.clientId,
      domain: withoutTrailingSlash(cognito.domain),
    },
  };
}

export async function loadConfig(fetcher: typeof fetch = (...args) => fetch(...args)): Promise<RuntimeConfig> {
  const response = await fetcher("/config.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Couldn't load site configuration (HTTP ${response.status})`);
  }
  return parseConfig(await response.json());
}

const ConfigContext = createContext<RuntimeConfig | null>(null);

export function ConfigProvider({ config, children }: { config: RuntimeConfig; children: ReactNode }) {
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

export function useConfig(): RuntimeConfig {
  const config = useContext(ConfigContext);
  if (!config) {
    throw new Error("useConfig must be used inside ConfigProvider");
  }
  return config;
}
